import {
  LOCALE_TAGS,
  LOCALES,
  formatMessage,
  isLocale,
  matchLocale,
  siteDefaultLocale,
  siteLocales,
  type Locale,
  type MessageParams,
} from '@aitoken/contracts';

/**
 * 用户端多语言运行时（无第三方依赖）。
 *
 * 文案按「命名空间 × 语言」一文件：src/i18n/messages/<ns>/<locale>.ts，default 导出扁平的 key → 文案。
 * zh-CN 是源语言（同步打包，也是缺词时的兜底）；其他语言按需懒加载，切换语言时整包加载该语言的所有命名空间。
 * 文案语法见 @aitoken/contracts formatMessage：{name} 插值、{n, plural, one {…} other {…}}、{x, select, …}；
 * 富文本用 <tag>…</tag>，由 t.rich 渲染。
 */
export type Dict = Record<string, string>;
/** 其他语言文件的类型：必须覆盖源语言的所有 key */
export type Translation<T> = { [K in keyof T]: string };

const eagerZh = import.meta.glob<{ default: Dict }>('./messages/*/zh-CN.ts', { eager: true });
const lazy = import.meta.glob<{ default: Dict }>(['./messages/*/*.ts', '!./messages/*/zh-CN.ts', '!./messages/*/index.ts']);

const nsOf = (path: string) => path.split('/').at(-2)!;
const catalogs: Partial<Record<Locale, Record<string, Dict>>> = {
  'zh-CN': Object.fromEntries(Object.entries(eagerZh).map(([p, m]) => [nsOf(p), m.default])),
};

const STORAGE_KEY = 'aitoken.locale';
/** 上次访问的站点（cn / global）：启动时就能知道国内站只有中文，不用等站点信息 */
const SITE_KEY = 'aitoken.site';
let current: Locale = 'zh-CN';
/** 本站提供的语言；国内站只有简体中文，切换语言无效、语言按钮隐藏 */
let available: readonly Locale[] = LOCALES;
let siteKnown = false;
/** 用户是否明确选过语言，或浏览器语言命中支持列表；否则站点信息到达后改用站点默认语言 */
let decided = false;
const listeners = new Set<() => void>();
const changeHooks = new Set<(l: Locale) => void>();

export function getLocale(): Locale {
  return current;
}
export function getLocaleTag(): string {
  return LOCALE_TAGS[current];
}
export function getAvailableLocales(): readonly Locale[] {
  return available;
}
/** 是否已知道本站（上次访问记住的，或站点信息已到达） */
export function isSiteKnown(): boolean {
  return siteKnown;
}

export async function loadLocale(locale: Locale): Promise<void> {
  if (catalogs[locale]) return;
  const entries = Object.entries(lazy).filter(([p]) => p.endsWith(`/${locale}.ts`));
  const mods = await Promise.all(entries.map(async ([p, load]) => [nsOf(p), (await load()).default] as const));
  catalogs[locale] = Object.fromEntries(mods);
}

export function translate(ns: string, key: string, params?: MessageParams): string {
  const raw = catalogs[current]?.[ns]?.[key] ?? catalogs['zh-CN']?.[ns]?.[key];
  if (raw == null) {
    if (import.meta.env.DEV) console.warn(`[i18n] missing ${ns}:${key}`);
    return key;
  }
  return formatMessage(raw, params, current);
}

function apply(locale: Locale) {
  current = locale;
  if (typeof document !== 'undefined') document.documentElement.lang = LOCALE_TAGS[locale];
  for (const h of changeHooks) h(locale);
  for (const l of listeners) l();
}

/** 切换语言：加载语言包后通知订阅者重渲染；persist=true 时记住选择 */
export async function setLocale(locale: Locale, { persist = true }: { persist?: boolean } = {}) {
  if (!available.includes(locale)) return;
  await loadLocale(locale);
  if (persist) {
    decided = true;
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      /* 隐私模式 */
    }
  }
  if (locale !== current) apply(locale);
}

/** 启动时：已保存的选择 → 浏览器语言 → 暂用简体中文（站点信息到达后再按站点兜底） */
export async function initLocale(): Promise<Locale> {
  let site: string | null = null;
  try {
    site = localStorage.getItem(SITE_KEY);
  } catch {
    /* ignore */
  }
  if (site === 'cn' || site === 'global') {
    siteKnown = true;
    available = siteLocales(site);
    if (available.length === 1) {
      decided = true;
      apply(available[0]!);
      return available[0]!;
    }
  }
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  // ?lang=vi 这类链接参数优先（便于分享与截图检查），并记住
  let fromUrl: string | null = null;
  try {
    fromUrl = new URLSearchParams(location.search).get('lang');
  } catch {
    /* ignore */
  }
  const urlLocale = matchLocale(fromUrl);
  if (urlLocale) {
    try {
      localStorage.setItem(STORAGE_KEY, urlLocale);
    } catch {
      /* ignore */
    }
  }
  let locale: Locale | null = urlLocale ?? (isLocale(stored) ? stored : null);
  if (locale) decided = true;
  else if (typeof navigator !== 'undefined') {
    for (const tag of navigator.languages ?? [navigator.language]) {
      locale = matchLocale(tag);
      if (locale) {
        decided = true;
        break;
      }
    }
  }
  locale ??= 'zh-CN';
  await loadLocale(locale);
  apply(locale);
  return locale;
}

/**
 * 站点信息到达：记住站点；只有一种语言的站点（国内站）固定用它；
 * 否则浏览器语言都不支持、用户也没选过时，改用站点默认语言（海外站英文）
 */
export async function applySiteDefault(siteId: 'cn' | 'global'): Promise<void> {
  if (!siteKnown) {
    siteKnown = true;
    try {
      localStorage.setItem(SITE_KEY, siteId);
    } catch {
      /* ignore */
    }
  }
  const before = available;
  available = siteLocales(siteId);
  if (available.length === 1) {
    decided = true;
    if (current !== available[0]) await setLocale(available[0]!, { persist: false });
    else if (before !== available) for (const l of listeners) l();
    return;
  }
  if (decided) return;
  decided = true;
  const l = siteDefaultLocale(siteId);
  if (l !== current) await setLocale(l, { persist: false });
}

export function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** 非 React 代码（格式化、zod 配置等）订阅语言变化 */
export function onLocaleChange(fn: (l: Locale) => void) {
  changeHooks.add(fn);
  return () => changeHooks.delete(fn);
}

export { LOCALES };
